import { useState, useEffect, useCallback, useRef } from "react";
import { save } from "./stamps";

const DEFAULT_STORAGE_KEY = "ppt_wantlist_v1";

/* One way to want a card. "Maybe" went first — a card you are unsure about
   just comes off the list — and PSA 10 has followed it: a list of cards you
   are after does not need to record how you would rather own one, and the
   control for it sat beside "Owned" looking like it said something about the
   card you already have. Grading is its own screen now.
   Anything still stored as psa10 or maybe reads as a plain want. */
export const WANT_STATUSES = ["want"];
export const WANT_STATUS_LABEL = { want: "Want" };
export const normalizeStatus = (s) => (WANT_STATUSES.includes(s) ? s : "want");

/**
 * A list of cards you're hunting. Used twice: an empty one you fill from search,
 * and the One Piece list which ships with its cards already in it.
 *
 * @param storageKey where to persist — each list needs its own so they can't collide
 * @param seed cards to start from when nothing has been saved yet
 */
export default function useWantList(storageKey = DEFAULT_STORAGE_KEY, seed = []) {
  const [wantList, setWantList] = useState(seed);
  // Saving is held off until the load has run, otherwise the first save fires
  // with the seed still in state and overwrites what was stored.
  const hydrated = useRef(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setWantList(parsed);
      }
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`Failed to load ${storageKey} from localStorage`, e);
      }
    }
    hydrated.current = true;
  }, [storageKey]);

  useEffect(() => {
    if (!hydrated.current) return;
    try {
      // Always written, even when empty. Removing the key would let a seeded
      // list resurrect every card you'd deliberately deleted.
      save(storageKey, JSON.stringify(wantList));
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`Failed to save ${storageKey} to localStorage`, e);
      }
    }
  }, [wantList, storageKey]);

  const addToWantList = useCallback((card, status = "want") => {
    setWantList((prev) => {
      if (prev.some((c) => c.id === card.id)) return prev;
      // Prices deliberately aren't carried over. This list is about whether you
      // want the card, not what it's worth — Collection is where value lives.
      const { prices, marketPrice, ...rest } = card;
      return [...prev, { ...rest, status, have: false }];
    });
  }, []);

  const removeFromWantList = useCallback((id) => {
    setWantList((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const cycleStatus = useCallback((id) => {
    setWantList((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const next = WANT_STATUSES[(WANT_STATUSES.indexOf(c.status) + 1) % WANT_STATUSES.length];
        return { ...c, status: next };
      })
    );
  }, []);

  const toggleHave = useCallback((id) => {
    setWantList((prev) => prev.map((c) => (c.id === id ? { ...c, have: !c.have } : c)));
  }, []);

  const isOnWantList = useCallback((id) => wantList.some((c) => c.id === id), [wantList]);

  // Counts up only: what's in the binder, and how long the list is. No completion
  // percentage and no "x of y" — the point is that it never counts what's missing.
  const haveCount = wantList.filter((c) => c.have).length;

  return {
    wantList,
    addToWantList,
    removeFromWantList,
    cycleStatus,
    toggleHave,
    isOnWantList,
    haveCount,
  };
}
